import { edgeSlabs, type LevelDef } from './levels';

/**
 * Rock that carries on past the sides of the underground and building maps. The renderer paints one
 * strip picture per side from the ground that runs into that edge, and repeats it as far out as the
 * view reaches. This module holds the numbers and the arithmetic, so they can be tested without a canvas.
 */

/** Width of a strip picture, in world px. It is repeated outward, so it only has to be wide enough to look like rock. */
export const STRIP_PX = 512;
/** Most tiles drawn on a side: 4096 world px past the edge, wider than any window the game is played in. */
export const MAX_STRIP_TILES = 8;
/** How far a tile reaches in under its neighbour (the first one under the map), so no hairline of backdrop shows where two pictures meet. */
export const STRIP_OVERLAP = 2;
/** Between turns the camera pulls out to the whole map, with this much to spare (see Renderer.updateCamera). */
export const FIT_MARGIN = 0.96;

/** Which sides of a level have rock that carries on past the edge. */
export function extendedSides(level: Pick<LevelDef, 'style' | 'width' | 'shapes'>): { left: boolean; right: boolean } {
  if (!level.style.extend) return { left: false, right: false };
  return { left: edgeSlabs(level, 'left').length > 0, right: edgeSlabs(level, 'right').length > 0 };
}

/**
 * The tiles needed to cover a view that reaches `reach` world px past an edge: for each, the distance from
 * the edge to its near side. Each reaches STRIP_OVERLAP px under the next one in, so a fractional zoom
 * leaves no hairline of backdrop between two of them. Nothing when the view stays inside the level.
 */
export function stripTiles(reach: number): number[] {
  if (!(reach > 0)) return [];
  const step = STRIP_PX - STRIP_OVERLAP;
  const n = Math.min(MAX_STRIP_TILES, Math.ceil(reach / step));
  return Array.from({ length: n }, (_, i) => i * step);
}

/**
 * How far past each edge the view reaches when the camera has pulled out to the whole map, for a canvas
 * of the given width to height ratio: the widest the view gets in play, since otherwise it is zoomed in.
 */
export function betweenTurnsReach(level: Pick<LevelDef, 'width' | 'height'>, aspect: number): number {
  const viewWidth = (aspect * level.height) / FIT_MARGIN;
  return Math.max(0, (viewWidth - level.width) / 2);
}
